import { Modal, Tabs } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import type { LiteQueryOut } from '@repo/api-codegen';
import { useApiGetCreationQueryV2 } from '@repo/api-codegen/v2';
import { EntityType } from '@repo/common/enums/entityType';
import { IconButton } from '@repo/foundations';
import { observer } from 'mobx-react-lite';
import type { ISecodaEntity } from '../../api';
import { useFmtSql } from '../../api/hooks/query/useFormatterQuery';
import { QueryCode } from '../QueryCodeBlock/QueryCode';

export interface IEntityCreationQueryProps {
	entity: ISecodaEntity;
}

function FormattedQueryCode({
	query,
	minHeight = 724,
}: {
	query: LiteQueryOut;
	minHeight?: number;
}) {
	const formattedSql = useFmtSql(query?.sql ?? '', query?.dialect ?? undefined);
	return (
		<QueryCode minHeight={minHeight} query={formattedSql ?? query?.sql ?? ''} />
	);
}

function EntityCreationQuery({ entity }: IEntityCreationQueryProps) {
	const { data: creationQueries = [] } = useApiGetCreationQueryV2(
		{
			pathParams: {
				entityId: entity.id,
			},
		},
		{
			enabled: !!entity.id,
		}
	);

	const [opened, { close, toggle }] = useDisclosure(false);

	if (
		!creationQueries.some((q) => q?.sql) ||
		entity.entity_type === EntityType.collection
	) {
		return null;
	}

	return (
		<>
			<IconButton
				tooltip="Creation queries"
				onClick={toggle}
				iconName="code"
				variant="tertiary"
			/>
			<Modal
				size="xl"
				opened={opened}
				onClose={close}
				title={`Creation queries - ${entity.title}`}
			>
				<Tabs defaultValue="0">
					<Tabs.List>
						{creationQueries.map((q, index) => (
							<Tabs.Tab key={q.hash} value={String(index)}>
								Query {index + 1}
							</Tabs.Tab>
						))}
					</Tabs.List>
					{creationQueries.map((q, index) => (
						<Tabs.Panel key={q.hash} value={String(index)} pt="md">
							<FormattedQueryCode query={q} minHeight={724} />
						</Tabs.Panel>
					))}
				</Tabs>
			</Modal>
		</>
	);
}

const EntityCreationQueryObserver = observer(EntityCreationQuery);
export default EntityCreationQueryObserver;
